import { safeFetchAPI } from '@/api';
import { log, logger, utils } from '@/helpers';
import { CampaignReportFilters, ConversionsReport, ListCampaignStatsByCampaignRequest, PaginatedData, PaginationReq } from '@/types';
import { create, StoreApi, UseBoundStore } from 'zustand';
import { LOCAL_CAMPAIGN_REPORT_TEST_TOKEN, LOCAL_CAMPAIGN_REPORT_TEST_UID } from './createCampaignReportFiltersStore';

export type CampaignReportConversionReportStoreState = {
	loaded: boolean;
	loading: boolean;
	conversionReport: ConversionsReport | null;
	campaignID: string;
	variantID: string | undefined;
};

export type CampaignReportConversionReportStoreActions = {
	fetchConversionReport: (req?: Partial<ListCampaignStatsByCampaignRequest>) => Promise<void>;
	reset: (filters?: CampaignReportFilters) => void;
};

export type CampaignReportConversionReportStore = CampaignReportConversionReportStoreState & CampaignReportConversionReportStoreActions;

const initialState: CampaignReportConversionReportStoreState = {
	loaded: false,
	loading: false,
	conversionReport: null,
	campaignID: 'global',
	variantID: undefined,
};

export const createCampaignReportConversionReportStore = (campaignID = 'global', variantID?: string, retailerCampaignID?: string) =>
	create<CampaignReportConversionReportStore>((set, get) => ({
		...initialState,
		campaignID,
		variantID,
		fetchConversionReport: async (req?: Partial<ListCampaignStatsByCampaignRequest>) => {
			set({ loading: true });

			const payload = { ...req };
			const endpoint = retailerCampaignID ? `/co-marketing/reports/conversions/:uid/${retailerCampaignID}` : '/conversions/:uid/reports';
			const [response, error] = await safeFetchAPI<ConversionsReport>(endpoint, {
				method: 'POST',
				payload,
				forceToken: LOCAL_CAMPAIGN_REPORT_TEST_TOKEN,
				uid: LOCAL_CAMPAIGN_REPORT_TEST_UID,
			});

			if (error) {
				utils?.toaster?.addToast?.({
					message: error || 'Error loading conversion report',
					type: 'danger',
				});
				set({
					loaded: true,
					loading: false,
					conversionReport: null,
				});
				return;
			}

			logger.log(endpoint).prefix(campaignID).add({ campaignID, variantID, retailerCampaignID }).addnl('response', response);

			set({
				loaded: true,
				loading: false,
				conversionReport: response ?? null,
			});
		},

		reset: (filters?: CampaignReportFilters) => {
			set(initialState);
			if (filters) {
				get().fetchConversionReport(filters);
			}
		},
	}));

const storeInstances = new Map<string, UseBoundStore<StoreApi<CampaignReportConversionReportStore>>>();

export const getCampaignReportConversionReportStore = (id = 'global', variantID?: string, retailerCampaignID?: string) => {
	const key = `${id}-${variantID}-${retailerCampaignID}`;
	if (!storeInstances.has(key)) {
		storeInstances.set(key, createCampaignReportConversionReportStore(id, variantID, retailerCampaignID));
	}
	return storeInstances.get(key)!;
};
